import { Owner } from 'src/state/models/owner';
import { ModuleKeys } from './modules';

/**
 * Subscription Plans (Synced with Backend)
 *
 * Product: ePOS - Complete Salon Management System
 * Plans: 4 tiers (1 Trial + 3 Paid)
 *
 * 1. Trial      - Full features (14 days)
 * 2. Starter    - Core POS with Walk-in, Gift Card, SMS (500)
 * 3. Business   - Starter + Appointment, Membership, eMarketing, SMS (2K)
 * 4. Enterprise - Business + Turn System, Advanced Reports, Payroll, SMS (5K) + Priority Support
 *
 * NOTE: No store limits - each store subscribes separately
 */
export enum OwnerPlan {
  Trial = 1,
  Starter = 2,
  Business = 3,
  Enterprise = 4,
}

// Trial: Full features for testing (14 days)
export const TrialPlanDisableModules: ModuleKeys[] = [];

// Starter: Essential POS features ($29.99)
export const StarterPlanDisableModules: ModuleKeys[] = [
  'schedule', // Appointment
  'turn_system',
  'membership',
  'emarketing',
  // Note: 'payroll' is part of 'report' module, handled separately
];

// Business: Adds appointment, membership, SMS allowance (2K)
export const BusinessPlanDisableModules: ModuleKeys[] = [
  'turn_system',
  // Note: 'payroll' is part of 'report' module, handled separately
];

// Enterprise: Full features + Premium support ($79.99)
export const EnterprisePlanDisableModules: ModuleKeys[] = [];

export const getDisableModulesByOwnerPlan = (owner: Owner) => {
  let disableModules: ModuleKeys[] = [];

  switch (owner.plan) {
    case OwnerPlan.Trial:
      disableModules = TrialPlanDisableModules;
      break;
    case OwnerPlan.Starter:
      disableModules = StarterPlanDisableModules;
      break;
    case OwnerPlan.Business:
      disableModules = BusinessPlanDisableModules;
      break;
    case OwnerPlan.Enterprise:
      disableModules = EnterprisePlanDisableModules;
      break;
    default:
      // Default to Starter limitations if plan not recognized
      disableModules = StarterPlanDisableModules;
      break;
  }

  // Plus modules: Modules can be enabled individually via owner settings
  // This allows for add-ons or special cases
  const plusModules: ModuleKeys[] = [];

  if (owner.enable_appointment) {
    plusModules.push('schedule');
  }
  if (owner.enable_walkin) {
    plusModules.push('walkin');
  }
  if (owner.enable_emarketing) {
    plusModules.push('emarketing');
  }
  if (owner.enable_sms) {
    plusModules.push('sms');
  }
  if (owner.enable_giftcard) {
    plusModules.push('giftcard');
  }
  if (owner.enable_pos) {
    plusModules.push('sale');
  }

  return disableModules.filter((item) => plusModules.indexOf(item) < 0);
};

/**
 * Get maximum employees allowed for the owner's plan
 * NOTE: All ePOS plans have UNLIMITED employees
 */
export const getMaximumEmployees = (owner: Owner) => {
  switch (owner.plan) {
    case OwnerPlan.Starter:
      return 12;
    case OwnerPlan.Business:
      return 24;
    default:
      return 999999999; // Trial & Enterprise treated as unlimited
  }
};

/**
 * Get SMS quota per month for the owner's plan
 */
export const getSMSQuota = (owner: Owner) => {
  switch (owner.plan) {
    case OwnerPlan.Trial:
      return 100; // Limited for trial (14 days)
    case OwnerPlan.Starter:
      return 500; // 500 SMS/month
    case OwnerPlan.Business:
      return 2000; // 2,000 SMS/month
    case OwnerPlan.Enterprise:
      return 5000; // 5,000 SMS/month
    default:
      return 0;
  }
};

/**
 * Check if payroll reports are available for the plan
 */
export const hasPayrollAccess = (owner: Owner) => {
  return owner.plan === OwnerPlan.Enterprise || owner.plan === OwnerPlan.Trial;
};

/**
 * Check if API/Web Service access is available
 */
export const hasAPIAccess = (owner: Owner) => {
  return owner.plan === OwnerPlan.Enterprise;
};

/**
 * Check if priority support is available
 */
export const hasPrioritySupport = (owner: Owner) => {
  return owner.plan === OwnerPlan.Enterprise;
};

/**
 * Check if advanced reports are available
 */
export const hasAdvancedReportsAccess = (owner: Owner) => {
  return owner.plan === OwnerPlan.Enterprise;
};

/**
 * Get plan name for display
 */
export const getPlanName = (planId: number): string => {
  switch (planId) {
    case OwnerPlan.Trial:
      return 'Trial';
    case OwnerPlan.Starter:
      return 'Starter';
    case OwnerPlan.Business:
      return 'Business';
    case OwnerPlan.Enterprise:
      return 'Enterprise';
    default:
      return 'Unknown';
  }
};
